import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Loader, Textarea } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import Navigation from '../Home/Navigation';
import { SEO } from '../SEO';
import seoConfig from '../SEO/seoConfig';
import styles from './TitleLanding.module.css';
import { useNotification } from '../../context/NotificationContext';
import { useAuth } from '../../context/Auth/AuthContext';
import { useTitleGeneration } from '../../context/TitleGenerationContext';
import { 
  fetchProjects, 
  Project, 
  deleteProject, 
  duplicateProject,
  ProjectStage
} from '../../services/projectService';
import { 
  generateTitlesFromVideo, 
  generateTitlesFromAudio, 
  generateTitlesFromText,
  getTitleData
} from '../../services/titleGenerationApi';
import ProcessingDashboard from '../ProcessingDashboard/ProcessingDashboard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faUpload, 
  faMicrophone, 
  faVideo, 
  faCalendarAlt, 
  faClock,
  faEllipsisVertical,
  faTrash,
  faDownload,
  faHeading
} from '@fortawesome/free-solid-svg-icons';

const TitleLanding: React.FC = () => {
  const { t } = useTranslation('titleLandingPage');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState<'all' | 'recent'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [projects, setProjects] = useState<Project[]>([]);
  const [isProjectsLoading, setIsProjectsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeActionDropdown, setActiveActionDropdown] = useState<string | null>(null);
  const [descriptionText, setDescriptionText] = useState('');
  const [projectsWithTitleData, setProjectsWithTitleData] = useState<Set<string>>(new Set());
  
  const { showNotification } = useNotification();
  const { token } = useAuth(); // Only get token for logging, ProtectedRoute handles auth
  const { openTitleModal } = useTitleGeneration();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Check if a project has title data
  const checkProjectTitleData = async (projectId: string): Promise<boolean> => {
    try {
      const titleData = await getTitleData(projectId);
      return titleData && (titleData.generatedTitles?.length > 0 || titleData.selectedTitle);
    } catch (error) {
      console.log(`Project ${projectId} has no title data`);
      return false; // No title data or error = no titles
    }
  };

  // Check title data for all projects
  const checkAllProjectsTitleData = async (projectsList: Project[]) => {
    const projectsWithTitles = new Set<string>();
    
    // Check each project for title data
    const titleChecks = projectsList.map(async (project) => {
      const hasTitleData = await checkProjectTitleData(project.id);
      if (hasTitleData) {
        projectsWithTitles.add(project.id);
      }
    });
    
    await Promise.all(titleChecks);
    setProjectsWithTitleData(projectsWithTitles);
  };

  // Fetch projects when component mounts
  useEffect(() => {
    console.log("useEffect - loading projects");
    console.log('🔑 Current auth token:', token ? 'Token present' : 'No token');
    
    let isMounted = true;
    let timeoutId: NodeJS.Timeout;
    
    const loadProjects = async () => {
      try {
        setIsProjectsLoading(true);
        setError(null);
        
        // Set a timeout to prevent infinite loading
        timeoutId = setTimeout(() => {
          if (isMounted) {
            console.log("Request timeout reached");
            setIsProjectsLoading(false);
            setError(t('titleLandingPage.projectsList.loadingError', { error: 'Request timed out. Please try again.' }));
            setProjects([]);
          }
        }, 10000); // 10 second timeout
        
        const response = await fetchProjects(1, 1000);
        
        if (!isMounted) return;
        
        // Clear the timeout since request completed
        clearTimeout(timeoutId);
        
        // If we get back response with projects property, use it even if empty
        if (response && 'projects' in response) {
          setProjects(response.projects || []);
          setError(null); // Clear any previous errors
          
          // Check title data for all projects
          if (response.projects && response.projects.length > 0) {
            await checkAllProjectsTitleData(response.projects);
          }
        } else {
          // If API returns unexpected format, set empty array and show a message
          setProjects([]);
          setError('Unexpected data format received');
          console.warn('API returned unexpected data format');
        }
      } catch (err: any) {
        if (!isMounted) return;
        
        console.error('Error fetching projects:', err);
        
        // Clear the timeout on error
        clearTimeout(timeoutId);
        
        // Handle different types of errors
        if (err?.message?.includes('401') || err?.message?.includes('Unauthorized')) {
          setError(t('titleLandingPage.projectsList.loadingError', { error: 'Authentication failed. Please log in again.' }));
          // Let ProtectedRoute handle the redirect
        } else {
          setError(t('titleLandingPage.projectsList.loadingError', { error: 'Failed to load projects. Please try again later.' }));
        }
        
        // Set empty projects array to prevent loading spinner from showing indefinitely
        setProjects([]);
      } finally {
        if (isMounted) {
          setIsProjectsLoading(false);
        }
      }
    };

    loadProjects();
    
    // Cleanup function
    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []); // Only run once on mount since ProtectedRoute handles auth

  // Trigger hidden file input when "Upload Video" is clicked
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || !event.target.files[0]) return;
    
    const videoFile = event.target.files[0];
    
    try {
      setIsLoading(true);
      setShowProcessing(true);
      setProcessingStatus('uploading');
      setProcessingMessage('Uploading your video...');
      
      // Small delay to show the uploading status
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setProcessingStatus('processing');
      setProcessingMessage('Processing your video content...');
      
      // Call the API to generate titles from video
      const projectId = await generateTitlesFromVideo(videoFile, descriptionText);
      
      setProcessingStatus('completed');
      setProcessingMessage('Video processed successfully!');
      
      // Short delay to show the completed status before navigating
      setTimeout(() => {
        // Pass the source of navigation ('title-landing') as a query parameter
        navigate(`/title-description/${projectId}?source=title-landing`);
      }, 1000);
    } catch (error) {
      console.error("Upload failed:", error);
      
      setProcessingStatus('error');
      setProcessingError('Failed to process your video. Please try again.');
      
      showNotification({
        title: t('titleLandingPage.notifications.uploadFailed'),
        message: t('titleLandingPage.notifications.uploadError'),
        type: 'error'
      });
      
      // Keep the processing dashboard visible for a moment to show the error
      setTimeout(() => {
        setShowProcessing(false);
        setIsLoading(false);
      }, 2000);
    }
  };

  // Toggle dropdown visibility
  const toggleActionDropdown = (projectId: string) => {
    setActiveActionDropdown(activeActionDropdown === projectId ? null : projectId);
  };

  // Handle project delete
  const handleDeleteProject = async (projectId: string) => {
    try {
      await deleteProject(projectId);
      setProjects(projects.filter(project => project.id !== projectId));
      showNotification({
        title: t('titleLandingPage.notifications.deleteSuccess'),
        message: t('titleLandingPage.notifications.deleteSuccess'),
        type: 'success'
      });
    } catch (error) {
      console.error("Delete failed:", error);
      showNotification({
        title: t('titleLandingPage.notifications.uploadFailed'),
        message: t('titleLandingPage.notifications.deleteError'),
        type: 'error'
      });
    }
  };

  // Handle project duplication
  const handleDuplicateProject = async (projectId: string) => {
    try {
      const newProject = await duplicateProject(projectId);
      setProjects([newProject, ...projects]);
      showNotification({
        title: t('titleLandingPage.notifications.duplicateSuccess'),
        message: t('titleLandingPage.notifications.duplicateSuccess'),
        type: 'success'
      });
    } catch (error) {
      console.error("Duplication failed:", error);
      showNotification({
        title: t('titleLandingPage.notifications.uploadFailed'),
        message: t('titleLandingPage.notifications.duplicateError'),
        type: 'error'
      });
    }
  };

  // State for processing dashboard
  const [showProcessing, setShowProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState<'uploading' | 'processing' | 'generating' | 'completed' | 'error'>('uploading');
  const [processingMessage, setProcessingMessage] = useState('');
  const [processingError, setProcessingError] = useState('');
  
  // Handle generate titles from description text
  const handleGenerateTitlesFromText = async () => {
    console.log("Generating");
    if (!descriptionText.trim()) {
      showNotification({
        title: t('errors.title', { ns: 'titleDescriptionPage' }),
        message: t('titleLandingPage.notifications.enterDescription'),
        type: 'error'
      });
      return;
    }
    
    try {
      setIsLoading(true);
      setShowProcessing(true);
      setProcessingStatus('generating');
      setProcessingMessage('Generating titles and descriptions from your text...');
      
      // Call the API to generate titles from text
      const projectId = await generateTitlesFromText(descriptionText);
      
      setProcessingStatus('completed');
      setProcessingMessage('Title generation complete!');
      
      // Short delay to show the completed status before navigating
      setTimeout(() => {
        // Pass the source of navigation ('title-landing') as a query parameter
        navigate(`/title-description/${projectId}?source=title-landing`);
      }, 1000);
    } catch (error) {
      console.error('Error generating titles:', error);
      setProcessingStatus('error');
      setProcessingError('Failed to generate titles. Please try again.');
      
      showNotification({
        title: t('errors.title', { ns: 'titleDescriptionPage' }),
        message: t('titleLandingPage.notifications.generateError'),
        type: 'error'
      });
      
      setIsLoading(false);
      // Keep the processing dashboard visible for a moment to show the error
      setTimeout(() => {
        setShowProcessing(false);
      }, 2000);
    }
  };

  // Handle continue working on project
  const handleContinueProject = (projectId: string) => {
    // Navigate to the title/description page for this project
    navigate(`/title-description/${projectId}`);
  };

  // Handle generate titles for projects without title data
  const handleGenerateTitles = (project: Project) => {
    console.log('Opening title modal for project:', project.id);
    
    if (project.originalVideoUrl || project.generatedVideoUrl) {
      // Open modal with video
      openTitleModal('video', project.originalVideoUrl || project.generatedVideoUrl, project.id);
    } else if (project.originalAudioUrl || project.finalAudio) {
      // Open modal with audio
      openTitleModal('audio', project.originalAudioUrl || project.finalAudio, project.id);
    } else {
      // Open modal for text-only generation
      openTitleModal('none', undefined, project.id);
    }
  };

  // Refresh projects and title data (called after successful title generation)
  const refreshProjectsData = async () => {
    try {
      const response = await fetchProjects(1, 1000);
      if (response && 'projects' in response) {
        setProjects(response.projects || []);
        if (response.projects && response.projects.length > 0) {
          await checkAllProjectsTitleData(response.projects);
        }
      }
    } catch (error) {
      console.error('Error refreshing projects:', error);
    }
  };

  // Calculate project counts for filter buttons
  const projectCounts = {
    all: projects.filter(project => {
      // For title landing page, show all title-related projects
      const isRelevantForTitlePage = project.originalVideoUrl || project.stage === 'TITLE_APP' || project.type === 'video';
      return isRelevantForTitlePage;
    }).length,
    recent: projects.filter(project => {
      // For title landing page, show all title-related projects
      const isRelevantForTitlePage = project.originalVideoUrl || project.stage === 'TITLE_APP' || project.type === 'video';
      if (!isRelevantForTitlePage) return false;
      
      // Get projects from the last 7 days
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const projectDate = new Date(project.date);
      return projectDate >= sevenDaysAgo;
    }).length
  };

  // Filter projects based on selectedOption, and searchTerm
  const filteredProjects = projects.filter(project => {
    // For title landing page, show all title-related projects
    // This includes projects with originalVideoUrl OR projects from title generation (which may not have video)
    // We can identify title projects by checking if they have stage "TITLE_APP" or if they were created through title generation
    const isRelevantForTitlePage = project.originalVideoUrl || project.stage === 'TITLE_APP' || project.type === 'video';
    
    if (!isRelevantForTitlePage) {
      return false;
    }
    
    // Filter by recent
    if (selectedOption === 'recent') {
      // Get projects from the last 7 days
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const projectDate = new Date(project.date);
      if (projectDate < sevenDaysAgo) return false;
    }
    
    // Filter by search term
    if (searchTerm && !project.title.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    
    return true;
  });

  // Function to format the date in a more readable way
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div className={styles.outerContainer}>
      <SEO
        title={seoConfig.titleGenerator.title}
        description={seoConfig.titleGenerator.description}
        keywords={seoConfig.titleGenerator.keywords}
        url="https://kingdomstudio.pro/title-landing"
      />
      <Navigation />
      
      {/* Processing Dashboard */}
      {showProcessing && (
        <ProcessingDashboard
          status={processingStatus}
          message={processingMessage}
          error={processingError}
          onComplete={() => {
            setShowProcessing(false);
            setIsLoading(false);
          }}
        />
      )}
      <div className={styles.container}>
        <main className={styles.main}>
          {/* Header Section */}
          <div className={styles.header}>
          <h1 className={styles.title}>
            <FontAwesomeIcon icon={faHeading} className={styles.titleIcon} />
            {t('titleLandingPage.hero.title')}
          </h1>
          <p className={styles.subtitle}>
            {t('titleLandingPage.hero.subtitle')}
          </p>
          
          {/* Action Buttons */}
          <div className={styles.headerActions}>
            <Button
              className={styles.createButton}
              onClick={handleUploadClick}
              leftSection={<FontAwesomeIcon icon={faVideo} />}
              loading={isLoading}
              size="lg"
            >
              {isLoading ? t('titleLandingPage.processingStatus.uploading') : t('titleLandingPage.hero.uploadButton')}
            </Button>
          </div>

          {/* Hidden file input */}
          <input
            type="file"
            accept="video/*"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </div>
        {/* Commented out description tile */}
        {/*
        <div className={styles.createOptions}>
          <div className={styles.cardOption}>
            <div className={styles.iconContainer}>
              <FontAwesomeIcon icon={faVideo} className={styles.optionIcon} />
            </div>
            <h3 className={styles.optionTitle}>Upload Video</h3>
            <p className={styles.optionDesc}>
              Upload a video to automatically generate titles and descriptions
            </p>
            <Button
              className={styles.optionButton}
              size="lg"
              onClick={handleUploadClick}
              loading={isLoading}
            >
              {!isLoading && <FontAwesomeIcon icon={faUpload} />} 
              {isLoading ? 'Uploading...' : 'Upload Video'}
            </Button>
          </div>
          
          <div className={styles.cardOption}>
            <div className={styles.iconContainer}>
              <FontAwesomeIcon icon={faHeading} className={styles.optionIcon} />
            </div>
            <h3 className={styles.optionTitle}>Enter Description</h3>
            <p className={styles.optionDesc}>
              Enter a description of your content to generate titles
            </p>
            <textarea
              style={{zIndex: 1000, width: '100%'}}
              placeholder="Enter a description of your video content..."
              className={styles.descriptionInput}
              rows={4}
              value={descriptionText}
              onChange={(e) => setDescriptionText(e.target.value)}
            />
            <Button
              className={styles.optionButton}
              size="lg"
              onClick={handleGenerateTitlesFromText}
              loading={isLoading}
            >
              {!isLoading && <FontAwesomeIcon icon={faPlus} />} 
              {isLoading ? 'Generating...' : 'Generate Titles'}
            </Button>
          </div>
        </div>
        */}
        
        {/* Projects Section */}
        <div className={styles.projectsSection}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>{t('titleLandingPage.projectsList.title')}</h2>
            <div className={styles.searchAndFilter}>
              <div className={styles.searchBar}>
                <input
                  type="text"
                  placeholder={t('titleLandingPage.projectsList.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={styles.searchInput}
                />
              </div>
              
              <div className={styles.filterOptions}>
                <button 
                  className={`${styles.filterButton} ${selectedOption === 'all' ? styles.active : ''}`}
                  onClick={() => setSelectedOption('all')}
                >
                  {t('titleLandingPage.projectsList.filters.all')} ({projectCounts.all})
                </button>
                <button 
                  className={`${styles.filterButton} ${selectedOption === 'recent' ? styles.active : ''}`}
                  onClick={() => setSelectedOption('recent')}
                >
                  {t('titleLandingPage.projectsList.filters.recent')} ({projectCounts.recent})
                </button>
              </div>
            </div>
          </div>
          
          {isProjectsLoading ? (
            <div className={styles.loadingContainer}>
              <Loader size="xl" />
              <p>{t('titleLandingPage.projectsList.loading')}</p>
            </div>
          ) : error ? (
            <div className={styles.errorContainer}>
              <p className={styles.errorMessage}>{error}</p>
              <Button onClick={() => window.location.reload()} className={styles.retryButton}>
                {t('titleLandingPage.projectsList.retry')}
              </Button>
            </div>
          ) : filteredProjects.length > 0 ? (
            <div className={styles.projectGrid}>
              {filteredProjects.map(project => (
                <div 
                  key={project.id} 
                  className={`${styles.projectCard} 
                    ${activeActionDropdown === project.id ? styles.cardWithActiveDropdown : ''}
                  `}
                >
                  <div className={styles.projectThumbnail}>
                    {/* Use video preview for projects with video URLs */}
                    {(project.originalVideoUrl || project.generatedVideoUrl) ? (
                      <video 
                        src={project.generatedVideoUrl || project.originalVideoUrl}
                        className={styles.videoPreview}
                        muted
                        preload="metadata"
                        onError={(e) => {
                          // Fallback to thumbnail image if video fails to load
                          const videoElement = e.target as HTMLVideoElement;
                          const img = document.createElement('img');
                          img.src = project.thumbnail || 'https://aiapptest2.s3.us-east-1.amazonaws.com/projects/library/images/1741139292210-aerial-view-of-wooden-bridge-over-the-river-fores-2023-11-27-05-11-54-utc.jpg';
                          img.alt = project.title;
                          img.className = styles.videoPreview;
                          img.onerror = () => {
                            img.src = 'https://aiapptest2.s3.us-east-1.amazonaws.com/projects/library/images/1741139292210-aerial-view-of-wooden-bridge-over-the-river-fores-2023-11-27-05-11-54-utc.jpg';
                          };
                          videoElement.parentNode?.replaceChild(img, videoElement);
                        }}
                      />
                    ) : (
                      <div 
                        className={styles.videoPreview}
                        style={{
                          backgroundImage: project.thumbnail ? `url(${project.thumbnail})` : undefined,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          background: project.thumbnail ? undefined : 'linear-gradient(135deg, #ff7e00, #ff2b48)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '24px',
                          color: '#ffffff'
                        }}
                      >
                        {!project.thumbnail && <FontAwesomeIcon icon={faHeading} size="2x" />}
                      </div>
                    )}
                    <div className={styles.projectType}>
                      <FontAwesomeIcon icon={faHeading} />
                    </div>
                    <div className={styles.projectDuration}>
                      <FontAwesomeIcon icon={faClock} />
                      <span>{project.duration}</span>
                    </div>
                  </div>
                  
                  <div className={styles.projectInfo}>
                    <h3 className={styles.projectTitle}>{project.title}</h3>
                    <div className={styles.projectMeta}>
                      <span className={styles.projectDate}>
                        <FontAwesomeIcon icon={faCalendarAlt} />
                        {formatDate(project.date)}
                      </span>
                      {!projectsWithTitleData.has(project.id) && (
                        <span style={{ color: '#888', fontSize: '12px', marginLeft: '8px' }}>
                          • {t('titleLandingPage.projectCard.needsTitles')}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className={styles.projectActions}>
                    {projectsWithTitleData.has(project.id) ? (
                      <Button 
                        className={styles.continueButton}
                        onClick={() => handleContinueProject(project.id)}
                      >
                        {t('titleLandingPage.projectCard.continueButton')}
                      </Button>
                    ) : (
                      <Button 
                        className={styles.continueButton}
                        onClick={() => handleGenerateTitles(project)}
                      >
                        {t('titleLandingPage.projectCard.generateButton')}
                      </Button>
                    )}
                    <div className={styles.moreActions}>
                      <button 
                        className={styles.moreButton}
                        onClick={() => toggleActionDropdown(project.id)}
                      >
                        <FontAwesomeIcon icon={faEllipsisVertical} />
                      </button>
                      {activeActionDropdown === project.id && (
                        <div className={`${styles.actionDropdown} ${activeActionDropdown === project.id ? styles.active : ''}`}
                             onClick={(e) => e.stopPropagation()} /* Prevent clicks from closing dropdown */
                        >
                          <button 
                            className={styles.actionButton}
                            onClick={() => handleDuplicateProject(project.id)}
                          >
                            {t('titleLandingPage.projectCard.duplicate')}
                          </button>
                          <button 
                            className={styles.actionButton}
                            onClick={() => handleDeleteProject(project.id)}
                          >
                            <FontAwesomeIcon icon={faTrash} className="fa-trash" /> {t('titleLandingPage.projectCard.delete')}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.noProjects}>
              <FontAwesomeIcon icon={faPlus} size="2x" className={styles.noProjectsIcon} />
              <h3>{t('titleLandingPage.projectsList.noProjects')}</h3>
              <p>{t('titleLandingPage.projectsList.noProjectsMessage')}</p>
              <Button 
                className={styles.createNewButton}
                onClick={handleUploadClick}
              >
                <FontAwesomeIcon icon={faUpload} style={{marginRight: '8px'}} /> {t('titleLandingPage.projectsList.createNewButton')}
              </Button>
            </div>
          )}
        </div>
        </main>
      </div>
    </div>
  );
};

export default TitleLanding;